import { useMemo, useState, useCallback, useTransition } from 'react';

import { Card, CardHeader } from '@/components/ui/Card';
import { PhaseChart, PHASE_META } from '@/components/charts/PhaseChart';
import type { PhaseKey, Metric } from '@/components/charts/PhaseChart';
import { SegmentedToggle } from '@/components/ui/SegmentedToggle';
import { formatNumber } from '@/lib/formatters';
import { mean, percentile, safe } from '@/lib/calculations';
import { sanitiseWindow } from '@/lib/aggregations';
import type { Granularity, TelemetryRow } from '@/types';
import { cn } from '@/lib/cn';
import {
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer,
} from 'recharts';

/* -------------------------------------------------------------------------- */
/*  Constants                                                                 */
/* -------------------------------------------------------------------------- */

interface DeepDiveProps {
  meterRows: TelemetryRow[];
}

// Voltage card: Today / This Week / This Month (calendar-aligned)
type VoltWin = 'today' | 'thisWeek' | 'thisMonth' | 'lastMonth';

const VOLT_WIN_OPTS: { value: VoltWin; label: string }[] = [
  { value: 'today',     label: 'Today'      },
  { value: 'thisWeek',  label: 'This Week'  },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
];

function voltWindowRows(rows: TelemetryRow[], w: VoltWin): TelemetryRow[] {
  if (!rows.length) return [];
  const anchor = rows[rows.length - 1].timestamp;
  const y = anchor.getFullYear(), mo = anchor.getMonth(), d = anchor.getDate();
  if (w === 'lastMonth') {
    const from = new Date(y, mo - 1, 1, 0, 0, 0, 0).getTime();
    const to   = new Date(y, mo,     1, 0, 0, 0, 0).getTime() - 1;
    return rows.filter(r => { const ts = r.timestamp.getTime(); return ts >= from && ts <= to; });
  }
  const from =
    w === 'today'     ? new Date(y, mo, d, 0, 0, 0, 0).getTime()
    : w === 'thisWeek'  ? new Date(y, mo, d - anchor.getDay(), 0, 0, 0, 0).getTime()
    : new Date(y, mo, 1, 0, 0, 0, 0).getTime();
  return rows.filter(r => r.timestamp.getTime() >= from);
}

function voltGranFor(w: VoltWin): Granularity {
  return w === 'today' ? 'hourly' : 'daily';
}

function voltWinLabel(w: VoltWin): string {
  return w === 'today'     ? 'today'
       : w === 'thisWeek'  ? 'this week'
       : w === 'thisMonth' ? 'this month'
       : 'last month';
}

function reconstructVoltagePhase(row: TelemetryRow): TelemetryRow | null {
  const r = row.vR;
  const y = row.vY;
  const b = row.vB;

  const rOk   = Number.isFinite(r) && r > 0;
  const yOk   = Number.isFinite(y) && y > 0;
  const bOk   = Number.isFinite(b) && b > 0;
  const avgOk = Number.isFinite(row.vlnAvg) && row.vlnAvg > 0;

  const presentCount  = [rOk, yOk, bOk].filter(Boolean).length;
  const missingCount  = 3 - presentCount;

  // Drop the row entirely if 2 or more phase values are missing/zero —
  // not enough data to reconstruct or display meaningfully.
  if (missingCount >= 2) return null;

  // Case 1: exactly one phase missing, vlnAvg present → derive the missing phase
  if (presentCount === 2 && avgOk) {
    const total = row.vlnAvg * 3;
    const next  = { ...row };
    if (!rOk) next.vR = Math.max(0, total - safe(y) - safe(b));
    if (!yOk) next.vY = Math.max(0, total - safe(r) - safe(b));
    if (!bOk) next.vB = Math.max(0, total - safe(r) - safe(y));
    return next;
  }

  // Case 2: all three phases present but vlnAvg missing/zero → derive vlnAvg
  if (presentCount === 3 && !avgOk) {
    return { ...row, vlnAvg: (safe(r) + safe(y) + safe(b)) / 3 };
  }

  return row;
}

function reconstructVoltageRows(rows: TelemetryRow[]): TelemetryRow[] {
  return rows
    .map((row) => reconstructVoltagePhase(row))
    .filter((row): row is TelemetryRow => row !== null);
}

/**
 * Mirror of reconstructVoltagePhase for current.
 *
 * currentTotal is the per-phase average (same relationship as vlnAvg):
 *   currentTotal * 3 = currentR + currentY + currentB
 *
 * Cases handled (exactly one missing field):
 *   - missing currentR/Y/B  → derive from the other two phases + currentTotal
 *   - missing currentTotal  → derive as mean(currentR, currentY, currentB)
 *
 * If more than one field is missing we leave the row untouched — there
 * isn't enough information to reconstruct reliably.
 */
function reconstructCurrentPhase(row: TelemetryRow): TelemetryRow | null {
  const r   = row.currentR;
  const y   = row.currentY;
  const b   = row.currentB;
  const avg = row.currentTotal;

  const rOk   = Number.isFinite(r)   && r   > 0;
  const yOk   = Number.isFinite(y)   && y   > 0;
  const bOk   = Number.isFinite(b)   && b   > 0;
  const avgOk = Number.isFinite(avg) && avg > 0;

  const phaseOkCount  = [rOk, yOk, bOk].filter(Boolean).length;
  const missingPhases = 3 - phaseOkCount;

  // Drop the row entirely if 2 or more phase values are missing/zero —
  // not enough data to reconstruct or display meaningfully.
  if (missingPhases >= 2) return null;

  // Case 1: exactly one phase missing, avg present → derive the missing phase
  if (missingPhases === 1 && avgOk) {
    const total = avg * 3;
    const next  = { ...row };
    if (!rOk) next.currentR = Math.max(0, total - safe(y) - safe(b));
    if (!yOk) next.currentY = Math.max(0, total - safe(r) - safe(b));
    if (!bOk) next.currentB = Math.max(0, total - safe(r) - safe(y));
    return next;
  }

  // Case 2: all three phases present but avg missing/zero → derive avg
  if (missingPhases === 0 && !avgOk) {
    return { ...row, currentTotal: (safe(r) + safe(y) + safe(b)) / 3 };
  }

  return row;
}

function reconstructCurrentRows(rows: TelemetryRow[]): TelemetryRow[] {
  return rows
    .map((row) => reconstructCurrentPhase(row))
    .filter((row): row is TelemetryRow => row !== null);
}

function clampVoltageRows(rows: TelemetryRow[]): TelemetryRow[] {
  if (!rows.length) return rows;

  const ceilingFor = (values: number[]): number => {
    const clean = values.filter((value) => Number.isFinite(value) && value > 0);
    return clean.length ? percentile(clean, 99.5) : 0;
  };

  const ceilings = {
    vR: ceilingFor(rows.map((row) => row.vR)),
    vY: ceilingFor(rows.map((row) => row.vY)),
    vB: ceilingFor(rows.map((row) => row.vB)),
    vlnAvg: ceilingFor(rows.map((row) => row.vlnAvg)),
  };

  if (!Object.values(ceilings).some((value) => value > 0)) return rows;

  return rows.map((row) => {
    let changed = false;

    const clampValue = (value: number, ceiling: number): number => {
      if (!(Number.isFinite(value) && value > 0 && ceiling > 0)) return value;
      const next = Math.min(value, ceiling);
      if (next !== value) changed = true;
      return next;
    };

    const vR = clampValue(row.vR, ceilings.vR);
    const vY = clampValue(row.vY, ceilings.vY);
    const vB = clampValue(row.vB, ceilings.vB);
    const avgPhases = [vR, vY, vB].filter((value) => Number.isFinite(value) && value > 0) as number[];
    const vlnAvg = avgPhases.length
      ? mean(avgPhases)
      : clampValue(row.vlnAvg, ceilings.vlnAvg);

    if (vlnAvg !== row.vlnAvg) changed = true;
    if (!changed) return row;

    return {
      ...row,
      vR,
      vY,
      vB,
      vlnAvg,
      isClamped: true,
    };
  });
}

// Current card uses the same calendar windows as Voltage

const ALL_PHASES: PhaseKey[] = ['R', 'Y', 'B', 'AVG'];

/* -------------------------------------------------------------------------- */
/*  Phase toggle pill                                                         */
/*  Each channel is an independent toggle — tap to show/hide.                */
/* -------------------------------------------------------------------------- */

function PhaseToggle({
  phaseKey,
  active,
  onToggle,
}: {
  phaseKey: PhaseKey;
  active: boolean;
  onToggle: (k: PhaseKey) => void;
}) {
  const meta = PHASE_META[phaseKey];
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onToggle(phaseKey)}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold',
        'border transition-all duration-200 select-none active:scale-95',
        active
          ? 'border-transparent text-white shadow-sm'
          : 'border-ink-200 text-ink-900 bg-white',
      )}
      style={active ? { backgroundColor: meta.color, borderColor: meta.color } : {}}
    >
      {/* Dash indicator for AVG */}
      {phaseKey === 'AVG' ? (
        <span className="w-3.5 h-px border-t border-dashed" style={{ borderColor: active ? 'white' : meta.color }} />
      ) : (
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? 'white' : meta.color }} />
      )}
      {phaseKey === 'AVG' ? 'Avg' : phaseKey}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*  Stat tile — per-phase summary                                             */
/* -------------------------------------------------------------------------- */

function PhaseStat({
  phaseKey, value, unit, dimmed,
}: {
  phaseKey: PhaseKey;
  value: number;
  unit: string;
  dimmed: boolean;
}) {
  const meta = PHASE_META[phaseKey];
  return (
    <div
      className={cn(
        'rounded-xl px-2 py-2.5 text-center transition-opacity duration-200',
        dimmed ? 'opacity-30' : 'opacity-100',
        'bg-ink-50',
      )}
    >
      <div className="flex items-center justify-center gap-1 mb-1">
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: meta.color }} />
        <span className="text-[11px] font-bold tracking-wide uppercase text-ink-900">{meta.label}</span>
      </div>
      <div className="num text-sm font-semibold text-ink-900">
        {formatNumber(value)}&thinsp;{unit}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Harmonic Histogram — THD & K-Factor per-phase distribution               */
/*                                                                            */
/*  Each card shows Phase R, Y, B as overlapping histogram bars per bucket.  */
/*  A Voltage / Current toggle switches which metric is displayed.           */
/*  Phase toggles (R / Y / B) mirror the Phase Voltage (L-N) card exactly.  */
/*  Y-axis = sample count (raw readings, as shown in the reference UI).      */
/*  Colors = PHASE_META: R #EF4444, Y #F59E0B, B #0891B2                    */
/* -------------------------------------------------------------------------- */

// ── Window types & helpers ────────────────────────────────────────────────────

type ThdWin = 'today' | 'thisWeek' | 'thisMonth' | 'lastMonth';

const HIST_WIN_OPTS: { value: ThdWin; label: string }[] = [
  { value: 'today',     label: 'Today'      },
  { value: 'thisWeek',  label: 'This Week'  },
  { value: 'thisMonth', label: 'This Month' },
  { value: 'lastMonth', label: 'Last Month' },
];

function thdWindowRows(rows: TelemetryRow[], w: ThdWin): TelemetryRow[] {
  if (!rows.length) return [];
  const anchor = rows[rows.length - 1].timestamp;
  const y = anchor.getFullYear(), mo = anchor.getMonth(), d = anchor.getDate();
  if (w === 'lastMonth') {
    const from = new Date(y, mo - 1, 1, 0, 0, 0, 0).getTime();
    const to   = new Date(y, mo,     1, 0, 0, 0, 0).getTime() - 1;
    return rows.filter(r => { const ts = r.timestamp.getTime(); return ts >= from && ts <= to; });
  }
  const from =
    w === 'today'
      ? new Date(y, mo, d,              0, 0, 0, 0).getTime()
      : w === 'thisWeek'
        ? new Date(y, mo, d - anchor.getDay(), 0, 0, 0, 0).getTime()
        : new Date(y, mo, 1, 0, 0, 0, 0).getTime();
  return rows.filter(r => r.timestamp.getTime() >= from);
}

function thdWinLabel(w: ThdWin): string {
  return w === 'today' ? 'today'
       : w === 'thisWeek'  ? 'this week'
       : w === 'thisMonth' ? 'this month'
       : 'last month';
}

// ── Phase color constants — exact PHASE_META values ──────────────────────────
//
// These must always stay in sync with PHASE_META in PhaseChart.tsx.
// R = #EF4444  Y = #F59E0B  B = #0891B2

type PhaseRYB = 'R' | 'Y' | 'B';
const PHASE_COLORS: Record<PhaseRYB, string> = {
  R: '#EF4444',
  Y: '#F59E0B',
  B: '#0891B2',
};
const ALL_RYB: PhaseRYB[] = ['R', 'Y', 'B'];

// ── THD buckets: 0–2, 2–4, …, 12–14, ≥14 % ─────────────────────────────────

const THD_BUCKETS: { label: string; lo: number; hi: number }[] = [
  { label: '0–2',   lo: 0,  hi: 2  },
  { label: '2–4',   lo: 2,  hi: 4  },
  { label: '4–6',   lo: 4,  hi: 6  },
  { label: '6–8',   lo: 6,  hi: 8  },
  { label: '8–10',  lo: 8,  hi: 10 },
  { label: '10–12', lo: 10, hi: 12 },
  { label: '12–14', lo: 12, hi: 14 },
  { label: '≥14',   lo: 14, hi: Infinity },
];

// ── K-Factor buckets: 0–1, 1–2, …, 9–10, ≥10 ───────────────────────────────

const KF_BUCKETS: { label: string; lo: number; hi: number }[] = [
  { label: '0–1',  lo: 0,  hi: 1  },
  { label: '1–2',  lo: 1,  hi: 2  },
  { label: '2–3',  lo: 2,  hi: 3  },
  { label: '3–4',  lo: 3,  hi: 4  },
  { label: '4–5',  lo: 4,  hi: 5  },
  { label: '5–6',  lo: 5,  hi: 6  },
  { label: '6–7',  lo: 6,  hi: 7  },
  { label: '7–8',  lo: 7,  hi: 8  },
  { label: '8–9',  lo: 8,  hi: 9  },
  { label: '9–10', lo: 9,  hi: 10 },
  { label: '≥10',  lo: 10, hi: Infinity },
];

// ── Per-phase histogram bin ───────────────────────────────────────────────────

interface PhaseHistBin {
  label: string;  // bucket range label
  R:     number;  // sample count for Phase R
  Y:     number;  // sample count for Phase Y
  B:     number;  // sample count for Phase B
}

/**
 * Build a per-phase sample-count histogram from a row window.
 *
 * @param rows       Filtered telemetry window
 * @param getPhase   Extract the THD/KF value for each phase from a row
 * @param buckets    THD_BUCKETS or KF_BUCKETS
 */
function buildPhaseHistogram(
  rows:     TelemetryRow[],
  getPhase: { R: (r: TelemetryRow) => number; Y: (r: TelemetryRow) => number; B: (r: TelemetryRow) => number },
  buckets:  typeof THD_BUCKETS,
): PhaseHistBin[] {
  const counts: Record<PhaseRYB, number[]> = {
    R: new Array(buckets.length).fill(0),
    Y: new Array(buckets.length).fill(0),
    B: new Array(buckets.length).fill(0),
  };

  for (const r of rows) {
    if (r.isDropout) continue;
    for (const ph of ALL_RYB) {
      const v = getPhase[ph](r);
      if (!(v > 0)) continue;
      for (let b = 0; b < buckets.length; b++) {
        const { lo, hi } = buckets[b];
        if (v >= lo && (hi === Infinity ? true : v < hi)) {
          counts[ph][b]++;
          break;
        }
      }
    }
  }

  return buckets.map((bkt, b) => ({
    label: bkt.label,
    R:     counts.R[b],
    Y:     counts.Y[b],
    B:     counts.B[b],
  }));
}

// ── Axis tick components — raw count Y-axis, bucket label X-axis ─────────────

function HistXTick({ x, y, payload }: { x?: number; y?: number; payload?: { value: string } }) {
  if (x == null || y == null) return null;
  return (
    <text x={x} y={y + 10} textAnchor="middle" fontSize={10} fontWeight="700" fill="#0F172A" fontFamily="inherit">
      {payload?.value}
    </text>
  );
}

function HistYTick({ x, y, payload }: { x?: number; y?: number; payload?: { value: number } }) {
  if (x == null || y == null) return null;
  const v = payload?.value ?? 0;
  const label = v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${Math.round(v)}`;
  return (
    <text x={x} y={y + 4} textAnchor="end" fontSize={10} fontWeight="700" fill="#64748B" fontFamily="inherit">
      {label}
    </text>
  );
}

// ── Phase toggle pill — mirrors PhaseToggle style in voltage card ─────────────

function PhaseTogglePill({
  phase, active, onToggle,
}: { phase: PhaseRYB; active: boolean; onToggle: (p: PhaseRYB) => void }) {
  const color = PHASE_COLORS[phase];
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onToggle(phase)}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold',
        'border transition-all duration-200 select-none active:scale-95',
        active ? 'border-transparent text-white shadow-sm' : 'border-ink-200 text-ink-900 bg-white',
      )}
      style={active ? { backgroundColor: color, borderColor: color } : {}}
    >
      <span
        className="w-1.5 h-1.5 rounded-full shrink-0"
        style={{ background: active ? 'white' : color }}
      />
      {phase}
    </button>
  );
}

// ── Overlapping per-phase histogram chart ─────────────────────────────────────

function PhaseHistogramChart({
  bins,
  activePhases,
  height = 180,
}: {
  bins:         PhaseHistBin[];
  activePhases: Set<PhaseRYB>;
  height?:      number;
}) {
  // Y-axis ceiling = max count across all visible phases
  const yMax = Math.max(
    ...bins.flatMap(b =>
      ALL_RYB.filter(p => activePhases.has(p)).map(p => b[p])
    ),
    1,
  );
  const yHigh = Math.ceil(yMax * 1.25);

  // Nice Y-axis ticks — pick a round step so we get 4–5 gridlines
  const rawStep = yHigh / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / magnitude;
  const niceStep = norm <= 1 ? magnitude
    : norm <= 2 ? 2 * magnitude
    : norm <= 5 ? 5 * magnitude
    : 10 * magnitude;
  const yTicks: number[] = [];
  for (let v = 0; v <= yHigh; v += niceStep) yTicks.push(Math.round(v));

  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <BarChart
          data={bins}
          margin={{ top: 4, right: 4, left: 4, bottom: 0 }}
          barCategoryGap="18%"
          barGap={-1}
        >
          <CartesianGrid stroke="#E2E8F0" vertical={false} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tick={<HistXTick />}
            interval={0}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={38}
            domain={[0, yHigh]}
            ticks={yTicks}
            tick={<HistYTick />}
          />
          <ReTooltip
            cursor={{ fill: 'rgba(15,177,138,0.06)' }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const visible = payload.filter(
                p => activePhases.has(p.dataKey as PhaseRYB) && (p.value as number) > 0
              );
              if (!visible.length) return null;
              return (
                <div className="rounded-xl bg-white shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[150px]">
                  <div className="font-bold text-slate-900 mb-1.5">{label}</div>
                  {visible.map(p => (
                    <div key={p.dataKey as string} className="flex items-center justify-between gap-3 mb-0.5">
                      <span className="flex items-center gap-1.5">
                        <span
                          className="inline-block w-2 h-2 rounded-sm shrink-0"
                          style={{ backgroundColor: PHASE_COLORS[p.dataKey as PhaseRYB] }}
                        />
                        <span className="font-semibold text-slate-700">
                          Phase {p.dataKey as string}
                        </span>
                      </span>
                      <span className="num font-bold text-slate-900">
                        {(p.value as number).toLocaleString()} readings
                      </span>
                    </div>
                  ))}
                </div>
              );
            }}
          />
          {/* Overlapping bars: each phase rendered as its own Bar with fillOpacity
              so the tallest bar is fully visible behind shorter ones.
              Bars share the same barCategoryGap slot — barGap=-1 pulls them flush
              so they visually overlap rather than sitting side-by-side. */}
          {ALL_RYB.filter(p => activePhases.has(p)).map(ph => (
            <Bar
              key={ph}
              dataKey={ph}
              name={`Phase ${ph}`}
              radius={[3, 3, 0, 0]}
              maxBarSize={40}
              fill={PHASE_COLORS[ph]}
              fillOpacity={0.80}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Shared peak-value status badge (matches reference UI) ────────────────────

function PeakBadge({ value, warn, crit, unit: _unit }: { value: number; warn: number; crit: number; unit: string }) {
  const label = value >= crit ? 'Critical · peak'
    : value >= warn ? 'Warning · peak'
    : 'Normal · peak';
  const cls = value >= crit
    ? 'bg-red-100 text-red-700'
    : value >= warn
      ? 'bg-amber-100 text-amber-700'
      : 'bg-emerald-100 text-emerald-700';
  return (
    <span className={cn('text-[11px] font-semibold px-2 py-0.5 rounded-full', cls)}>
      {label}
    </span>
  );
}

// ── ThdHistogramCard ──────────────────────────────────────────────────────────

/**
 * THD distribution histogram for LK4410 meters.
 *
 * Shows per-phase (R / Y / B) overlapping bars for Voltage or Current THD,
 * switchable via a Voltage / Current toggle — matching the reference UI.
 * Hidden automatically when no THD register has non-zero values.
 */
function ThdHistogramCard({ meterRows }: { meterRows: TelemetryRow[] }) {
  type ThdMetric = 'voltage' | 'current';

  const [metric,       setMetric]       = useState<ThdMetric>('voltage');
  const [win,          setWin]          = useState<ThdWin>('thisWeek');
  const [activePhases, setActivePhases] = useState<Set<PhaseRYB>>(new Set(ALL_RYB));
  const [isPending,    startTransition] = useTransition();

  // ── Data availability ────────────────────────────────────────────────────
  const hasThdData = useMemo(() =>
    meterRows.some(r => safe(r.vRThdPct) > 0 || safe(r.iRThdPct) > 0),
    [meterRows],
  );

  // ── Window rows ──────────────────────────────────────────────────────────
  const windowRows = useMemo(() => thdWindowRows(meterRows, win), [meterRows, win]);

  // ── Accessors per metric ─────────────────────────────────────────────────
  const voltageAccessors = {
    R: (r: TelemetryRow) => safe(r.vRThdPct),
    Y: (r: TelemetryRow) => safe(r.vYThdPct),
    B: (r: TelemetryRow) => safe(r.vBThdPct),
  };
  const currentAccessors = {
    R: (r: TelemetryRow) => safe(r.iRThdPct),
    Y: (r: TelemetryRow) => safe(r.iYThdPct),
    B: (r: TelemetryRow) => safe(r.iBThdPct),
  };
  const accessors = metric === 'voltage' ? voltageAccessors : currentAccessors;

  // ── Histogram bins ────────────────────────────────────────────────────────
  const bins = useMemo(
    () => buildPhaseHistogram(windowRows, accessors, THD_BUCKETS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [windowRows, metric],
  );

  // ── Peak value (max across active phases) ────────────────────────────────
  const peakValue = useMemo(() => {
    let max = 0;
    for (const r of windowRows) {
      if (r.isDropout) continue;
      for (const ph of ALL_RYB) {
        if (!activePhases.has(ph)) continue;
        const v = accessors[ph](r);
        if (v > max) max = v;
      }
    }
    return max;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowRows, metric, activePhases]);

  const sampleCount = windowRows.filter(r => !r.isDropout).length;
  const readingCount = sampleCount * 3; // 3 phases per sample

  const handleMetric = useCallback((next: string) => {
    startTransition(() => setMetric(next as ThdMetric));
  }, []);
  const handleWin = useCallback((next: string) => {
    startTransition(() => setWin(next as ThdWin));
  }, []);
  const togglePhase = useCallback((p: PhaseRYB) => {
    setActivePhases(prev => {
      if (prev.has(p) && prev.size === 1) return prev; // keep at least one
      const next = new Set(prev);
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });
  }, []);

  if (!hasThdData) return null;

  const metricLabel = metric === 'voltage' ? 'Voltage' : 'Current';
  // Warn/crit thresholds: Voltage THD >5% warn, >8% crit; Current >10% warn, >20% crit
  const warnThresh = metric === 'voltage' ? 5  : 10;
  const critThresh = metric === 'voltage' ? 15 : 20;

  return (
    <Card>
      {/* ── Header ── */}
      <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-1">
        {metricLabel} Total Harmonic Distortion (%)
      </p>

      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="flex items-baseline gap-2">
            <span className="num text-2xl font-extrabold text-ink-900 leading-none">
              {peakValue > 0 ? `${peakValue.toFixed(1)}%` : '—'}
            </span>
            {peakValue > 0 && (
              <PeakBadge value={peakValue} warn={warnThresh} crit={critThresh} unit="%" />
            )}
          </div>
          <p className="text-[10px] text-ink-400 mt-0.5">
            Per-phase · {thdWinLabel(win)}
          </p>
        </div>
        {/* Window selector */}
        <SegmentedToggle
          size="sm"
          value={win}
          onChange={handleWin}
          options={HIST_WIN_OPTS}
          ariaLabel="THD window"
        />
      </div>

      {/* ── Voltage / Current toggle (left) + Phase R/Y/B pills (right) ──
           Mirrors the Phase Voltage (L-N) card layout exactly:
           same flex row, same pill size, same gap. */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <SegmentedToggle
          size="sm"
          value={metric}
          onChange={handleMetric}
          options={[
            { value: 'voltage', label: 'Voltage' },
            { value: 'current', label: 'Current' },
          ]}
          ariaLabel="THD metric"
        />
        <div className="flex items-center gap-1.5" role="group" aria-label="THD phase channels">
          {ALL_RYB.map(ph => (
            <PhaseTogglePill
              key={ph}
              phase={ph}
              active={activePhases.has(ph)}
              onToggle={togglePhase}
            />
          ))}
        </div>
      </div>

      {/* ── Sub-label ── */}
      <p className="text-[10px] font-bold tracking-wide uppercase text-ink-500 mb-2">
        {metricLabel} THD distribution · sample count per 2% band
      </p>

      {/* ── Chart ── */}
      {isPending ? (
        <div className="h-44 flex items-center justify-center">
          <span className="text-xs text-ink-400">Loading…</span>
        </div>
      ) : (
        <PhaseHistogramChart bins={bins} activePhases={activePhases} height={175} />
      )}

      {/* ── Footer ── */}
      <div className="mt-2 flex items-center justify-between text-[10px] text-ink-400">
        <div className="flex items-center gap-3">
          {ALL_RYB.map(ph => (
            <span key={ph} className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: PHASE_COLORS[ph] }} />
              <span className="font-semibold">Phase {ph}</span>
            </span>
          ))}
        </div>
        <div className="text-right num">
          <div>{readingCount.toLocaleString()} readings · 3 phases</div>
          <div>{sampleCount.toLocaleString()} samples · {thdWinLabel(win)}</div>
        </div>
      </div>
    </Card>
  );
}

// ── KFactorHistogramCard ──────────────────────────────────────────────────────

/**
 * K-Factor distribution histogram for LK4410 meters.
 *
 * Shows per-phase (R / Y / B) overlapping bars for Voltage or Current K-Factor,
 * switchable via a Voltage / Current toggle — matching the reference UI.
 * Hidden automatically when no K-Factor register has non-zero values.
 */
function KFactorHistogramCard({ meterRows }: { meterRows: TelemetryRow[] }) {
  type KfMetric = 'voltage' | 'current';

  const [metric,       setMetric]       = useState<KfMetric>('voltage');
  const [win,          setWin]          = useState<ThdWin>('thisWeek');
  const [activePhases, setActivePhases] = useState<Set<PhaseRYB>>(new Set(ALL_RYB));
  const [isPending,    startTransition] = useTransition();

  // ── Data availability ────────────────────────────────────────────────────
  const hasKfData = useMemo(() =>
    meterRows.some(r => safe(r.kFactorVR) > 0 || safe(r.kFactorIR) > 0),
    [meterRows],
  );

  // ── Window rows ──────────────────────────────────────────────────────────
  const windowRows = useMemo(() => thdWindowRows(meterRows, win), [meterRows, win]);

  // ── Accessors per metric ─────────────────────────────────────────────────
  const voltageAccessors = {
    R: (r: TelemetryRow) => safe(r.kFactorVR),
    Y: (r: TelemetryRow) => safe(r.kFactorVY),
    B: (r: TelemetryRow) => safe(r.kFactorVB),
  };
  const currentAccessors = {
    R: (r: TelemetryRow) => safe(r.kFactorIR),
    Y: (r: TelemetryRow) => safe(r.kFactorIY),
    B: (r: TelemetryRow) => safe(r.kFactorIB),
  };
  const accessors = metric === 'voltage' ? voltageAccessors : currentAccessors;

  // ── Histogram bins ────────────────────────────────────────────────────────
  const bins = useMemo(
    () => buildPhaseHistogram(windowRows, accessors, KF_BUCKETS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [windowRows, metric],
  );

  // ── Peak value (max across active phases) ────────────────────────────────
  const peakValue = useMemo(() => {
    let max = 0;
    for (const r of windowRows) {
      if (r.isDropout) continue;
      for (const ph of ALL_RYB) {
        if (!activePhases.has(ph)) continue;
        const v = accessors[ph](r);
        if (v > max) max = v;
      }
    }
    return max;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowRows, metric, activePhases]);

  const sampleCount = windowRows.filter(r => !r.isDropout).length;
  const readingCount = sampleCount * 3;

  const handleMetric = useCallback((next: string) => {
    startTransition(() => setMetric(next as KfMetric));
  }, []);
  const handleWin = useCallback((next: string) => {
    startTransition(() => setWin(next as ThdWin));
  }, []);
  const togglePhase = useCallback((p: PhaseRYB) => {
    setActivePhases(prev => {
      if (prev.has(p) && prev.size === 1) return prev;
      const next = new Set(prev);
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });
  }, []);

  if (!hasKfData) return null;

  const metricLabel = metric === 'voltage' ? 'Voltage' : 'Current';
  // K>4 = warning, K≥10 = severe (matching alertDefs.ts thresholds)
  const warnThresh = 4;
  const critThresh = 10;

  return (
    <Card>
      {/* ── Header ── */}
      <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-1">
        {metricLabel} K-Factor
      </p>

      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="flex items-baseline gap-2">
            <span className="num text-2xl font-extrabold text-ink-900 leading-none">
              {peakValue > 0 ? peakValue.toFixed(2) : '—'}
            </span>
            {peakValue > 0 && (
              <PeakBadge value={peakValue} warn={warnThresh} crit={critThresh} unit="" />
            )}
          </div>
          <p className="text-[10px] text-ink-400 mt-0.5">
            Per-phase · {thdWinLabel(win)}
          </p>
        </div>
        {/* Window selector */}
        <SegmentedToggle
          size="sm"
          value={win}
          onChange={handleWin}
          options={HIST_WIN_OPTS}
          ariaLabel="K-Factor window"
        />
      </div>

      {/* ── Voltage / Current toggle (left) + Phase R/Y/B pills (right) ──
           Mirrors the Phase Voltage (L-N) card layout exactly. */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <SegmentedToggle
          size="sm"
          value={metric}
          onChange={handleMetric}
          options={[
            { value: 'voltage', label: 'Voltage' },
            { value: 'current', label: 'Current' },
          ]}
          ariaLabel="K-Factor metric"
        />
        <div className="flex items-center gap-1.5" role="group" aria-label="K-Factor phase channels">
          {ALL_RYB.map(ph => (
            <PhaseTogglePill
              key={ph}
              phase={ph}
              active={activePhases.has(ph)}
              onToggle={togglePhase}
            />
          ))}
        </div>
      </div>

      {/* ── Sub-label ── */}
      <p className="text-[10px] font-bold tracking-wide uppercase text-ink-500 mb-2">
        {metricLabel} K-Factor distribution · sample count per unit band
      </p>

      {/* ── Chart ── */}
      {isPending ? (
        <div className="h-44 flex items-center justify-center">
          <span className="text-xs text-ink-400">Loading…</span>
        </div>
      ) : (
        <PhaseHistogramChart bins={bins} activePhases={activePhases} height={175} />
      )}

      {/* ── Footer ── */}
      <div className="mt-2 flex items-center justify-between text-[10px] text-ink-400">
        <div className="flex items-center gap-3">
          {ALL_RYB.map(ph => (
            <span key={ph} className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: PHASE_COLORS[ph] }} />
              <span className="font-semibold">Phase {ph}</span>
            </span>
          ))}
        </div>
        <div className="text-right num">
          <div>{readingCount.toLocaleString()} readings · 3 phases</div>
          <div>{sampleCount.toLocaleString()} samples · {thdWinLabel(win)}</div>
        </div>
      </div>
    </Card>
  );
}

export function DeepDive({ meterRows }: DeepDiveProps) {
  const [metric,   setMetric]   = useState<Metric>('voltage');
  const [voltWin,  setVoltWin]  = useState<VoltWin>('today');
  const [currWin,  setCurrWin]  = useState<VoltWin>('today');
  // Each phase is independently toggleable; all on by default.
  const [visible, setVisible] = useState<Set<PhaseKey>>(new Set(ALL_PHASES));

  // useTransition defers the heavy re-render when switching granularity,
  // keeping the toggle animation instant and lag-free.
  const [isPending, startTransition] = useTransition();

  const handleCurrWin = useCallback((next: VoltWin) => {
    startTransition(() => setCurrWin(next));
  }, []);

  const handleVoltWin = useCallback((next: VoltWin) => {
    startTransition(() => setVoltWin(next));
  }, []);

  const handleMetric = useCallback((next: Metric) => {
    startTransition(() => setMetric(next));
  }, []);

  const togglePhase = useCallback((k: PhaseKey) => {
    setVisible((prev) => {
      // Always keep at least one channel visible
      if (prev.has(k) && prev.size === 1) return prev;
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  }, []);

  // Window rows — voltage uses calendar window, current uses granularity window
  const voltRows    = useMemo(
    () => clampVoltageRows(reconstructVoltageRows(voltWindowRows(meterRows, voltWin))),
    [meterRows, voltWin],
  );
  const currRows    = useMemo(() => reconstructCurrentRows(sanitiseWindow(voltWindowRows(meterRows, currWin))), [meterRows, currWin]);

  const activeRows = metric === 'voltage' ? voltRows : currRows;

  // ── Global rows for stable Y-axis scaling ─────────────────────────────────
  // Combine all voltage windows so the Y scale is fixed to the global
  // max/min regardless of which window is selected. We use the furthest-back
  // window (lastMonth) as the superset — it includes thisMonth, thisWeek, today.
  const allVoltRows = useMemo(
    () => clampVoltageRows(reconstructVoltageRows(voltWindowRows(meterRows, 'lastMonth'))),
    [meterRows],
  );

  // Use lastMonth as the global scale reference for current — widest window,
  // so the Y-axis stays stable across Today / This Week / This Month / Last Month.
  const allCurrRows = useMemo(
    () => reconstructCurrentRows(sanitiseWindow(voltWindowRows(meterRows, 'lastMonth'))),
    [meterRows],
  );

  // Active global rows — switches only when the metric (Voltage/Current) switches,
  // NOT when the time window switches.
  const globalScaleRows = metric === 'voltage' ? allVoltRows : allCurrRows;

  const stats = useMemo(() => {
    if (!activeRows.length) return { R: 0, Y: 0, B: 0, AVG: 0 };
    return metric === 'voltage'
      ? {
          R:   mean(activeRows.map((r) => r.vR)),
          Y:   mean(activeRows.map((r) => r.vY)),
          B:   mean(activeRows.map((r) => r.vB)),
          AVG: mean(activeRows.map((r) => r.vlnAvg)),
        }
      : {
          R:   mean(activeRows.map((r) => r.currentR)),
          Y:   mean(activeRows.map((r) => r.currentY)),
          B:   mean(activeRows.map((r) => r.currentB)),
          AVG: mean(activeRows.map((r) => r.currentTotal)),
        };
  }, [activeRows, metric]);

  const unit = metric === 'voltage' ? 'V' : 'A';

  // Voltage imbalance indicator
  const vImbalance = useMemo(() => {
    if (metric !== 'voltage' || !activeRows.length) return null;
    const avg = stats.AVG;
    if (avg <= 0) return null;
    const maxDev = Math.max(
      Math.abs(stats.R - avg),
      Math.abs(stats.Y - avg),
      Math.abs(stats.B - avg),
    );
    return (maxDev / avg) * 100;
  }, [metric, stats, activeRows]);

  return (
    <>
    <section className="space-y-3">
      <h2 className="font-display text-lg font-semibold text-ink-900 px-1">Deep dive</h2>

      <Card>
        {/* ── Header row ─────────────────────────────────────────────────── */}
        <p className="text-[11px] font-bold tracking-wide uppercase text-ink-900 mb-0.5">{metric === 'voltage' ? 'Phase voltage (L-N)' : 'Phase current'}</p>

        <CardHeader
          title={
            <span className="flex items-baseline gap-2">
              <span>{formatNumber(stats.AVG)}&thinsp;{unit}</span>
              {/* Imbalance badge */}
              {vImbalance !== null && vImbalance > 1 && (
                <span className={cn(
                  'text-[11px] font-semibold px-1.5 py-0.5 rounded-full leading-none',
                  vImbalance > 3
                    ? 'bg-signal-danger/10 text-signal-danger'
                    : 'bg-signal-warn/10 text-signal-warn',
                )}>
                  {vImbalance.toFixed(2)}% imbal
                </span>
              )}
            </span>
          }
          description={`Per-phase · ${metric === 'voltage' ? voltWinLabel(voltWin) : voltWinLabel(currWin)}${isPending ? ' · updating…' : ''}`}
          trailing={
            metric === 'voltage' ? (
              <SegmentedToggle
                size="sm"
                value={voltWin}
                onChange={v => handleVoltWin(v as VoltWin)}
                options={VOLT_WIN_OPTS}
                ariaLabel="Voltage period"
              />
            ) : (
              <SegmentedToggle
                size="sm"
                value={currWin}
                onChange={v => handleCurrWin(v as VoltWin)}
                options={VOLT_WIN_OPTS}
                ariaLabel="Current period"
              />
            )
          }
        />

        {/* ── Metric toggle + channel toggles ────────────────────────────── */}
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <SegmentedToggle
            size="sm"
            value={metric}
            onChange={handleMetric}
            options={[
              { value: 'voltage', label: 'Voltage' },
              { value: 'current', label: 'Current' },
            ]}
            ariaLabel="Phase metric"
          />

          {/* Channel toggles */}
          <div className="flex items-center gap-1.5" role="group" aria-label="Phase channels">
            {ALL_PHASES.map((k) => (
              <PhaseToggle
                key={k}
                phaseKey={k}
                active={visible.has(k)}
                onToggle={togglePhase}
              />
            ))}
          </div>
        </div>

        {/* ── Chart ─────────────────────────────────────────────────────── */}
        <div
          className={cn(
            'transition-opacity duration-300',
            isPending && 'opacity-50 pointer-events-none',
          )}
        >
          {activeRows.length ? (
            <PhaseChart
              rows={activeRows}
              metric={metric}
              granularity={metric === 'voltage' ? voltGranFor(voltWin) : voltGranFor(currWin)}
              visiblePhases={visible}
              height={270}
              globalRows={globalScaleRows}
            />
          ) : (
            <div className="flex items-center justify-center h-40 text-xs text-ink-900">
              No samples in {metric === 'voltage' ? voltWinLabel(voltWin) : voltWinLabel(currWin)}
            </div>
          )}
        </div>

        {/* ── Per-phase stat strip ───────────────────────────────────────── */}
        <div className="mt-3 grid grid-cols-4 gap-2">
          {ALL_PHASES.map((k) => (
            <PhaseStat
              key={k}
              phaseKey={k}
              value={stats[k]}
              unit={unit}
              dimmed={!visible.has(k)}
            />
          ))}
        </div>

        {/* ── Sample count footnote ─────────────────────────────────────── */}
        <p className="mt-2 text-[10px] text-ink-900 text-right num">
          {activeRows.length.toLocaleString()} samples · {metric === 'voltage' ? voltWinLabel(voltWin) : voltWinLabel(currWin)}
        </p>
      </Card>

      {/* ── THD histogram — LK4410 only, hidden automatically when no THD data ─── */}
      <ThdHistogramCard meterRows={meterRows} />

      {/* ── K-Factor histogram — LK4410 only, hidden automatically when no K-Factor data ─── */}
      <KFactorHistogramCard meterRows={meterRows} />

    </section>

    </>
  );
}